/**
 * 采购计划物资清单：同规格 ID / 同商品不限规格 不允许重复，添加/修改时自动合并数量。
 * 代购下单、购物车、订单编辑请使用 OrderItemsTable（允许同规格多行）。
 */
import OrderItemsTable, {
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  UNLIMITED_SPEC_LABEL,
  type OrderItem,
  type ProductPickPayload,
} from '@/pages/order/components/OrderItemsTable';
import { stripPasteMatchFailRemark } from '@/pages/proxy-order/components/PasteCodesModal';
import type { ComponentProps } from 'react';

export {
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  createEmptyOrderItem,
  UNLIMITED_SPEC_LABEL,
  isUnlimitedSpecItem,
  SpecCell,
  type OrderItem,
  type OrderItemSpecOption,
  type ProductPickPayload,
} from '@/pages/order/components/OrderItemsTable';

type OrderItemsTableProps = ComponentProps<typeof OrderItemsTable>;

const REMARK_MAX = 200;

function mergeRemarks(a?: string, b?: string): string {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (!left) return right.slice(0, REMARK_MAX);
  if (!right || left === right) return left.slice(0, REMARK_MAX);
  return `${left}；${right}`.slice(0, REMARK_MAX);
}

/** 规格限购用 s:specId；不限定规格用 g:goodsId */
function itemMatchKey(item: Pick<OrderItem, 'productId' | 'specId'>): string {
  const specId = String(item.specId || '').trim();
  if (specId) return `s:${specId}`;
  const goodsId = String(item.productId || '').trim();
  if (goodsId) return `g:${goodsId}`;
  return '';
}

function findMatchIndex(items: OrderItem[], key: string, exceptIndex = -1): number {
  if (!key) return -1;
  return items.findIndex(
    (item, i) => i !== exceptIndex && !isEmptyOrderItem(item) && itemMatchKey(item) === key,
  );
}

/** 按规格/商品键写入：已存在则累加数量并合并备注，否则插入空行/末尾 */
export function upsertOrderItemBySpec(
  items: OrderItem[],
  incoming: OrderItem,
): { items: OrderItem[]; merged: boolean } {
  const key = itemMatchKey(incoming);
  if (!key) {
    const emptyIndex = items.findIndex((item) => isEmptyOrderItem(item));
    const next = [...items];
    if (emptyIndex >= 0) next[emptyIndex] = incoming;
    else next.push(incoming);
    return { items: ensureSingleTrailingEmpty(next), merged: false };
  }

  const existingIndex = findMatchIndex(items, key);
  if (existingIndex >= 0) {
    const next = [...items];
    const cur = next[existingIndex];
    next[existingIndex] = {
      ...cur,
      productId: incoming.productId || cur.productId,
      name: incoming.name || cur.name,
      image: incoming.image ?? cur.image,
      specId: String(incoming.specId || '').trim() || undefined,
      spec: incoming.spec ?? cur.spec,
      unitPrice: Number.isFinite(incoming.unitPrice) ? incoming.unitPrice : cur.unitPrice,
      unit: incoming.unit || cur.unit,
      quantity: Math.max(1, (Number(cur.quantity) || 0) + (Number(incoming.quantity) || 0)),
      itemRemark: mergeRemarks(cur.itemRemark, incoming.itemRemark),
      specOptions: incoming.specOptions?.length ? incoming.specOptions : cur.specOptions,
    };
    return { items: ensureSingleTrailingEmpty(next), merged: true };
  }

  const emptyIndex = items.findIndex((item) => isEmptyOrderItem(item));
  const next = [...items];
  if (emptyIndex >= 0) next[emptyIndex] = incoming;
  else next.push(incoming);
  return { items: ensureSingleTrailingEmpty(next), merged: false };
}

/** 修改某一行后若键冲突，则合并到已有行并删除当前行 */
export function patchOrderItemMergingSpec(
  items: OrderItem[],
  index: number,
  patch: Partial<OrderItem>,
): { items: OrderItem[]; merged: boolean } {
  if (index < 0 || index >= items.length) {
    return { items, merged: false };
  }

  const updated: OrderItem = {
    ...items[index],
    ...patch,
  };
  // 显式传入的 specId（含空串）用于清除/切换为不限定规格；不能用 !== undefined 判断
  if (Object.prototype.hasOwnProperty.call(patch, 'specId')) {
    const nextSpecId = String(patch.specId || '').trim();
    updated.specId = nextSpecId || undefined;
  }
  const key = itemMatchKey(updated);

  if (!key || isEmptyOrderItem(updated)) {
    const next = [...items];
    next[index] = updated;
    return { items: ensureSingleTrailingEmpty(next), merged: false };
  }

  const dupIndex = findMatchIndex(items, key, index);
  if (dupIndex < 0) {
    const next = [...items];
    next[index] = updated;
    return { items: ensureSingleTrailingEmpty(next), merged: false };
  }

  const next = [...items];
  const target = next[dupIndex];
  next[dupIndex] = {
    ...target,
    productId: updated.productId || target.productId,
    name: updated.name || target.name,
    image: updated.image ?? target.image,
    specId: String(updated.specId || '').trim() || undefined,
    spec: updated.spec ?? target.spec,
    unitPrice: Number.isFinite(updated.unitPrice) ? updated.unitPrice : target.unitPrice,
    unit: updated.unit || target.unit,
    quantity: Math.max(1, (Number(target.quantity) || 0) + (Number(updated.quantity) || 0)),
    itemRemark: mergeRemarks(target.itemRemark, updated.itemRemark),
    specOptions: updated.specOptions?.length ? updated.specOptions : target.specOptions,
  };
  next.splice(index, 1);
  return { items: ensureSingleTrailingEmpty(next), merged: true };
}

/** 将 ProductPickPayload 应用到指定行，并按键去重合并 */
export function applyProductPickMergingSpec(
  items: OrderItem[],
  index: number,
  product: ProductPickPayload,
): { items: OrderItem[]; merged: boolean } {
  const current = items[index];
  return patchOrderItemMergingSpec(items, index, {
    productId: product.productId,
    name: product.name,
    image: product.image || '',
    specId: product.specId,
    spec: product.spec,
    unitPrice: product.unitPrice,
    unit: product.unit,
    quantity: current && current.quantity > 0 ? current.quantity : 1,
    specOptions: product.specs || [],
    itemRemark: stripPasteMatchFailRemark(current?.itemRemark),
  });
}

/** 采购计划专用清单表（UI 复用 OrderItemsTable，业务上强制同规格/同商品合并） */
export default function UniqueSpecOrderItemsTable(props: OrderItemsTableProps) {
  return <OrderItemsTable {...props} allowUnlimitedSpec />;
}
